import {Component, Injector, Input} from '@angular/core';
import {animate, state, transition, trigger, style} from '@angular/animations';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, NgControl,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {AbstractComponent} from "../abstract.component";
import {required} from "@utility";

export type OptionKey = number | string;
export type OptionLike = { label: string, selected: boolean};
export type OptionValue = string | OptionLike;


@Component({
    selector: 'app-multiselect-dropdown',
    templateUrl: './multiselect-dropdown.component.html',
    styleUrls: ['./multiselect-dropdown.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: MultiSelectDropdownComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: MultiSelectDropdownComponent
        }],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})
export class MultiSelectDropdownComponent extends AbstractComponent implements ControlValueAccessor, Validators {
  protected readonly SEARCH_ELEMENT_BREAKPOINT = 10;

  @Input() options: Map<OptionKey, OptionValue> = new Map<OptionKey, OptionValue>();

  @Input() dropdownLabel: string = "";

  @Input() maxItemsCount: number | undefined;
  @Input() maxItemsDisplay: number | undefined;
  @Input() isButton: boolean = false;
  @Input() buttonIconClass: string | undefined;
  @Input() isTableHeading: boolean = false;
  @Input() required: boolean = false;
  @Input() iconClass: string | undefined;
  @Input() isDropdownListFullWidth: boolean = false;
  /// Disable turning off all items(at least one item should stay selected)
  @Input() isDiabledAllTurnedOff: boolean = false;
  @Input() iconSize: number = 16;

  displayMaxItemsMessage:boolean = false;
  selectedValues: OptionKey[] = [];

  searchText: string | undefined
  filteredOptions: Map<OptionKey, OptionValue> = new Map<OptionKey, OptionValue>();

  ngOnInit() {
    this.filteredOptions = new Map<OptionKey, OptionValue>(this.options);
  }

  onChange = (selectedValues: any) => {
  };

  onTouched: () => void = () => {
  };

  touched: boolean = false;
  disabled: boolean = false;
  invalid: boolean = false;
  errors: ValidationErrors | null = null;

  constructor(private inj: Injector) {
    super();
  }

  ngAfterViewInit() {
    const outerControl: AbstractControl | null = this.inj.get(NgControl).control;
    if (outerControl) {
      outerControl.markAsTouched = () => {
        this.touched = true;
      };
      outerControl.markAsUntouched = () => {
        this.touched = false
      };
    }
  }

  writeValue(selectedValues: OptionKey[] | Map<OptionKey, OptionValue> | null | undefined) {
    if (Array.isArray(selectedValues)) {
      this.selectedValues = selectedValues;
      return;
    }

    if (selectedValues instanceof Map) {
      this.selectedValues = [];
      selectedValues.forEach((value, key) => {
        if (this.isOptionLike(value) && value.selected) {
          this.selectedValues.push(key);
        }
      })
      return;
    }

    this.selectedValues = [];
  }

  private isOptionLike(v: unknown): v is OptionLike {
    return (
        v !== null &&
        typeof v === 'object' &&
        'label' in v &&
        typeof (v as any).label === 'string' &&
        'selected' in v &&
        typeof (v as any).selected === 'boolean'
    );
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }


  onItemSelect(item: number | string) {
    let selectedOptions = this.selectedValues ? [...this.selectedValues] : [];
    if (this.isDiabledAllTurnedOff && this.selectedValues.length === 1) {
      return;
    }
    let index: number = selectedOptions.indexOf(item, 0);
    if (index > -1) {
      selectedOptions.splice(index, 1);
    } else {
      if (this.maxItemsCount && this.maxItemsCount === selectedOptions.length) {
        this.displayMaxItemsMessage = true;
      } else {
        this.displayMaxItemsMessage = false;
        selectedOptions.push(item);
      }
    }
    this.selectedValues = [...selectedOptions]
    this.onChange(this.selectedValues);
  }

  get formControlValue(): (number | string)[] {
    if (
      this.selectedValues
      && Array.isArray(this.selectedValues)) {
      return [...this.selectedValues]
    } else {
      return [];
    }
  }

  onSelectAll() {
    if (this.isDiabledAllTurnedOff && this.selectedValues.length === this.options.size) {
      return;
    }
    if (this.formControlValue.length === this.options.size) {
      this.selectedValues = []
    } else {
      this.selectedValues = [...this.options.keys()]
    }
    this.onChange(this.selectedValues);
  }

  isOptionSelected(item: number | string): boolean {
    return this.formControlValue.includes(item);
  }

  getOptionLabel(key: number | string): string {
    const v = this.options.get(key);
    if (typeof v === 'string') return v;
    return v?.label ?? '';
  }

  multiselectDropdownOpenChange(open: boolean) {
    this.markAsTouched();
    this.onSearch("");
  }

  onSearch(raw: string) {
    this.searchText = raw;
    const q = (raw ?? '').toLowerCase().trim();

    const filtered = new Map<OptionKey, OptionValue>();

    for (const [key, value] of this.options.entries()) {
      const label = this.getOptionLabel(key).toLowerCase();

      if (!q || label.includes(q)) {
        filtered.set(key, value);
      }
    }

    this.filteredOptions = filtered;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if ((control.value === undefined || control.value === null || control.value?.length === 0) && (control.hasValidator(required) || this.required)) {
      this.invalid = true;
      this.errors = {
        required: true
      };
      return this.errors;
    } else {
      this.errors = null;
      this.invalid = false;
      return this.errors;
    }
  }
}
